import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import {
  type AIContextItem,
  SystemContextProvider,
  type WorkflowContext,
} from '@gitlab-org/ai-context';
import { ParsedCliInput } from '../parse';
import { renderImportedSessionContext } from '../session_import/imported_session_renderer';
import { readSessionImportBundle } from '../session_import/session_import_reader';

export const IMPORTED_SESSION_ITEM_ID = 'agent_user_environment_imported_session';

@Implements(SystemContextProvider)
@Service({
  dependencies: [Logger, ParsedCliInput],
  lifetime: ServiceLifetime.Singleton,
})
export class ImportedSessionContextProvider implements SystemContextProvider {
  #logger: Logger;

  #cwd: string;

  #importPath?: string;

  constructor(logger: Logger, cliInput: ParsedCliInput) {
    this.#logger = withPrefix(logger, '[ImportedSessionContextProvider]');
    this.#cwd = cliInput.cwd;
    const { command } = cliInput;
    this.#importPath = command.name === 'tui' ? command.importSession : undefined;
  }

  async getItems(context?: WorkflowContext): Promise<AIContextItem[]> {
    // Startup only: on a resume the summary is already part of the session's history.
    if (!this.#importPath || !context || context.source !== 'startup') {
      return [];
    }

    // Consumed here, not in the constructor: this singleton outlives the session
    // the flag named, and `/new` starts another one with source 'startup'.
    const importPath = this.#importPath;
    this.#importPath = undefined;

    try {
      const bundle = await readSessionImportBundle(importPath, this.#cwd);
      const content = renderImportedSessionContext(bundle, { workspaceRoot: context.cwd });

      this.#logger.info(
        `Imported session ${bundle.session.id} as context (${content.length} chars) for session ${context.sessionId}`,
      );

      return [
        {
          category: 'agent_user_environment',
          content,
          id: IMPORTED_SESSION_ITEM_ID,
          metadata: {
            title: 'Imported Session',
            enabled: true,
            subType: 'snippet',
            icon: 'history',
            secondaryText: 'Summary of a session imported with --import-session',
            subTypeLabel: 'Imported session',
          },
        },
      ];
    } catch (error) {
      this.#logger.error('Failed to import session context', error as Error);
      return [];
    }
  }
}
